import type { PostPaymentOrderError, PostPaymentOrderRequest } from '@dtp/membership-service-types';
import { apiClient, ApiType, HTTP_STATUS, isAxiosError } from '@shared/apiClient';
import { createServerFieldMappings } from '../../lib/createServerFieldMappings';
import {
  type DocumentFormData,
  getDynamicFormErrorMessage,
  getOrchestrationId,
  isSuccessfulDynamicFormStatus,
  submitDocumentForm,
} from '../../lib/documentForm.utils';
import { IFK_CAMPAIGN_CODE } from '../../forms/holder/MembershipBundle/MembershipBundle.utils';

export const IDP_ARTICLE_SLUG = 'internasjonalt-forerkort';
export const PAYMENT_VALIDATE_URL = 'v2/products/payment/validate';
export const UNKNOWN_DOCUMENT_SUBMIT_ERROR = 'Det oppstod en ukjent feil ved innsending av dokumenter.';
export const ORCHESTRATION_ERROR = 'Det oppstod en ukjent feil ved innsending av dokumenter. Vennligst prøv igjen.';
export const UNKNOWN_VALIDATION_ERROR = 'Det oppstod en feil ved validering. Prøv igjen.';
export const UNKNOWN_GENERAL_ERROR = 'Det oppstod en ukjent feil. Prøv igjen senere.';
export const UNKNOWN_SERVER_VALIDATION_ERROR = 'Det oppstod en ukjent feil, prøv igjen om litt.';

export type DocumentSubmissionResult =
  | { result: 'success'; orchestrationId?: string }
  | { result: 'validationError'; validationError?: string }
  | { result: 'unknownError'; validationError: string };

export type PaymentValidationResult =
  | { result: 'success' }
  | { result: 'validationError'; validationError: PostPaymentOrderError }
  | { result: 'requestError' };

export const getStatusText = (
  isValidating: boolean,
  isSubmittingForms: boolean,
  isCreatingOrder: boolean,
  isCreated: boolean,
  tempError: string | undefined,
) => {
  if (isValidating || isSubmittingForms) {
    return 'Validerer og sender...';
  }
  if (isCreatingOrder) {
    return 'Sender ordren';
  }
  if (isCreated) {
    return 'Ordren er sendt, du blir nå videresendt til betaling.';
  }
  return tempError || '';
};

export const getCampaignCode = (hasSelectedMembership: boolean, campaignCode?: string): string | undefined =>
  hasSelectedMembership ? IFK_CAMPAIGN_CODE : campaignCode;

export const getNumberOfProducts = (amount?: string | number): number => parseInt(`${amount ?? 1}`, 10) || 1;

export const getPaymentValidationErrorStep = (
  firstErrorField: string | undefined,
  numberOfProducts: number,
  hasSelectedMembership: boolean,
): number => {
  if (!firstErrorField) {
    return 2;
  }

  const mapping = createServerFieldMappings(numberOfProducts);

  if (mapping.form1[firstErrorField]) {
    return 1;
  }

  if (mapping.form2[firstErrorField]) {
    return 2;
  }

  if (mapping.form3[firstErrorField]) {
    return 3;
  }

  if (hasSelectedMembership && firstErrorField.startsWith('CustomerProductsRequests[0].Consents')) {
    return 4;
  }

  return 2;
};

export const hasPaymentValidationResults = (data: unknown): data is PostPaymentOrderError =>
  typeof data === 'object' &&
  data !== null &&
  'validationResults' in data &&
  Array.isArray((data as { validationResults?: unknown }).validationResults);

export const submitDocumentFormForOrchestration = async (
  documentEndpoint: string,
  documentForm: DocumentFormData,
): Promise<DocumentSubmissionResult> => {
  try {
    const formResponse = await submitDocumentForm(documentEndpoint, documentForm);

    if (!isSuccessfulDynamicFormStatus(formResponse.status)) {
      return { result: 'success' };
    }

    return {
      result: 'success',
      orchestrationId: getOrchestrationId(formResponse.data),
    };
  } catch (error) {
    if (isAxiosError(error) && error.response) {
      return {
        result: 'validationError',
        validationError: getDynamicFormErrorMessage(error.response.data),
      };
    }

    return {
      result: 'unknownError',
      validationError: UNKNOWN_DOCUMENT_SUBMIT_ERROR,
    };
  }
};

export const validatePaymentOrder = async (
  paymentOrderPayload: PostPaymentOrderRequest,
): Promise<PaymentValidationResult> => {
  try {
    await apiClient.post(ApiType.apimMembershipApi, {
      url: PAYMENT_VALIDATE_URL,
      data: paymentOrderPayload,
      withCredentials: true,
    });

    return { result: 'success' };
  } catch (error) {
    if (isAxiosError(error) && error.response) {
      const { data, status } = error.response;

      if (status === HTTP_STATUS.UNPROCESSABLE_ENTITY && hasPaymentValidationResults(data)) {
        return {
          result: 'validationError',
          validationError: data,
        };
      }
    }

    return { result: 'requestError' };
  }
};
